import { useEffect } from 'react';
import './SludgeWaveAnimation.css';

/**
 * PROFESSIONAL SLUDGE WAVE ANIMATION
 * Poison-type special move - sends a toxic wave crashing over opponents
 * 
 * Key Features:
 * - Poison-type special attack (95 base power, 100% accuracy)
 * - 10% chance to poison
 * - Massive poison wave sweeping across the field
 * - Professional gaming quality with viscous toxic wave effects
 */
export default function SludgeWaveAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();

    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();

    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    const elements = [];

    // PHASE 1: Toxic Summoning (0-500ms)
    playerEl.classList.add('sludge-wave-user-charge');

    const toxicAura = document.createElement('div');
    toxicAura.className = 'sludge-wave-toxic-aura';
    toxicAura.style.left = `${playerX - 80}px`;
    toxicAura.style.top = `${playerY - 80}px`;
    container.appendChild(toxicAura);
    elements.push(toxicAura);

    // Toxic bubbles rising
    for (let i = 0; i < 12; i++) {
      const bubble = document.createElement('div');
      bubble.className = 'sludge-wave-bubble';
      bubble.style.left = `${playerX + (Math.random() - 0.5) * 80 - 5}px`;
      bubble.style.top = `${playerY + (Math.random() - 0.5) * 50 - 5}px`;
      bubble.style.animationDelay = `${i * 35}ms`;
      container.appendChild(bubble);
      elements.push(bubble);
    }

    // PHASE 2: Toxic Wave Formation (500-1100ms)
    const phase2Timer = setTimeout(() => {
      // The sludge wave
      const wave = document.createElement('div');
      wave.className = 'sludge-wave-wave';
      wave.style.left = `${playerX - 120}px`;
      wave.style.top = `${playerY - 50}px`;
      wave.style.setProperty('--distance', `${distance}px`);
      container.appendChild(wave);
      elements.push(wave);

      // Toxic splash particles along path
      for (let i = 0; i < 18; i++) {
        const splashTimer = setTimeout(() => {
          const splash = document.createElement('div');
          splash.className = 'sludge-wave-splash';
          const progress = i / 18;
          splash.style.left = `${playerX + dx * progress + (Math.random() - 0.5) * 40 - 6}px`;
          splash.style.top = `${playerY + dy * progress + (Math.random() - 0.5) * 30 - 6}px`;
          container.appendChild(splash);
          elements.push(splash);
        }, i * 30);
        elements.push({ timer: splashTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Toxic Impact (1100-1600ms)
    const phase3Timer = setTimeout(() => {
      opponentEl.classList.add('sludge-wave-opponent-hit');

      // Toxic explosion
      const explosion = document.createElement('div');
      explosion.className = 'sludge-wave-explosion';
      explosion.style.left = `${opponentX - 110}px`;
      explosion.style.top = `${opponentY - 110}px`;
      container.appendChild(explosion);
      elements.push(explosion);

      // Sludge splatter burst
      for (let i = 0; i < 20; i++) {
        const splatAngle = (i / 20) * 360;
        const splatDist = 55 + Math.random() * 50;
        const splat = document.createElement('div');
        splat.className = 'sludge-wave-splatter';
        splat.style.left = `${opponentX - 5}px`;
        splat.style.top = `${opponentY - 5}px`;
        splat.style.setProperty('--scatter-x', `${Math.cos(splatAngle * Math.PI / 180) * splatDist}px`);
        splat.style.setProperty('--scatter-y', `${Math.sin(splatAngle * Math.PI / 180) * splatDist}px`);
        splat.style.animationDelay = `${i * 12}ms`;
        container.appendChild(splat);
        elements.push(splat);
      }

      // Toxic shockwaves
      for (let i = 0; i < 3; i++) {
        const swTimer = setTimeout(() => {
          const sw = document.createElement('div');
          sw.className = 'sludge-wave-shockwave';
          sw.style.left = `${opponentX - 70}px`;
          sw.style.top = `${opponentY - 70}px`;
          container.appendChild(sw);
          elements.push(sw);
        }, i * 100);
        elements.push({ timer: swTimer, type: 'timer' });
      }
    }, 1100);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Poison Aftermath (1600-2000ms)
    const phase4Timer = setTimeout(() => {
      playerEl.classList.remove('sludge-wave-user-charge');

      // Toxic puddle and lingering poison
      const puddle = document.createElement('div');
      puddle.className = 'sludge-wave-puddle';
      puddle.style.left = `${opponentX - 60}px`;
      puddle.style.top = `${opponentY + 20}px`;
      container.appendChild(puddle);
      elements.push(puddle);

      for (let i = 0; i < 6; i++) {
        const linger = document.createElement('div');
        linger.className = 'sludge-wave-linger';
        linger.style.left = `${opponentX + (Math.random() - 0.5) * 80 - 6}px`;
        linger.style.top = `${opponentY + (Math.random() - 0.5) * 50 - 6}px`;
        linger.style.animationDelay = `${i * 40}ms`;
        container.appendChild(linger);
        elements.push(linger);
      }
    }, 1600);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (2000ms)
    const cleanupTimer = setTimeout(() => {
      playerEl.classList.remove('sludge-wave-user-charge');
      opponentEl.classList.remove('sludge-wave-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
      onComplete();
    }, 2000);

    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('sludge-wave-user-charge');
      opponentEl.classList.remove('sludge-wave-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') clearTimeout(item.timer);
        else if (item && item.parentNode) item.remove();
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

SludgeWaveAnimation.moveType = 'poison';
